import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Search, Plus, Edit, Trash2, Bot, Package, Clipboard, Upload, X, Loader2, Save, AlertTriangle, Check, PlusCircle, TrendingUp, Truck, Image as ImageIcon, Link2, ShoppingCart, RefreshCw, Store, Sparkles } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { type ColumnDef } from '@tanstack/react-table';
import { PageHeader } from '@/components/admin';
import { Button, Select, Card, Modal, Spinner } from '@/components/common';
import { Input } from '@/components/ui/input';
import { DataTable } from '@/components/ui/data-table';
import ProductAIAssistant from '@/components/admin/ProductAIAssistant';
import OciImportPanel from '@/components/admin/OciImportPanel';
import ProductBatchModal from '@/components/admin/ProductBatchModal';
import ProductUrlModal from '@/components/admin/ProductUrlModal';
import PriceComparison from '@/components/admin/PriceComparison';
import { useProducts, useProductCategories } from '@/hooks/useProducts';
import { products as productsApi } from '@/utils/api';
import { toast } from 'sonner';
import api from '@/utils/api';

// Konstanten
const UNITS = ['Meter', 'Stück', 'Rolle', 'kg', 'Liter', 'Paket', 'Set'];
const CATEGORIES = [
  'Kabel',
  'Leitungen',
  'Schalter',
  'Steckdosen',
  'Verteiler',
  'Beleuchtung',
  'Werkzeug',
  'Befestigung',
  'Rohre',
  'Schutzschaltgeräte',
  'Einbaugeräte',
  'Schaltgeräte',
  'Brandschutz',
  'Kommunikationstechnik',
  'Sonstiges',
];

// Kürzel für kompakte Tabellenansicht
const CATEGORY_SHORT = {
  'Kabel': 'Kab.',
  'Leitungen': 'Ltg.',
  'Schalter': 'Sch.',
  'Steckdosen': 'Std.',
  'Verteiler': 'Vtl.',
  'Beleuchtung': 'Bel.',
  'Werkzeug': 'Wrk.',
  'Befestigung': 'Bef.',
  'Rohre': 'Rhr.',
  'Schutzschaltgeräte': 'FI/LS',
  'Einbaugeräte': 'Einb.',
  'Schaltgeräte': 'Schg.',
  'Brandschutz': 'Brd.',
  'Kommunikationstechnik': 'Kom.',
  'Sonstiges': 'Son.',
};

const UNIT_SHORT = {
  'Meter': 'm',
  'Stück': 'Stk',
  'Stück': 'Stk',
  'Rolle': 'Rl',
  'kg': 'kg',
  'Liter': 'L',
  'Paket': 'Pkg',
  'Set': 'Set',
};

// Lieferanten-Codes (müssen mit Backend übereinstimmen)
const SUPPLIERS = [
  { code: 'obeta', name: 'Obeta', short: 'Ob.' },
  { code: 'sonepar', name: 'Sonepar', short: 'So.' },
  { code: 'unielektro', name: 'UniElektro', short: 'UE.' },
  { code: 'zajadacz', name: 'Zajadacz', short: 'Za.' },
];

// Helper: Lieferanten-Info aus sources extrahieren
const getSupplierInfo = (sources, supplierName) => {
  if (!sources || !Array.isArray(sources)) return null;
  return sources.find(s =>
    s.supplier?.toLowerCase().includes(supplierName.toLowerCase())
  );
};

// Column definitions factory (needs modal setters from component scope)
function produkteColumns({
  setPriceModal,
  setEditModal,
  setDeleteModal,
}: {
  setPriceModal: (v: { open: boolean; product: any }) => void;
  setEditModal: (v: { open: boolean; product: any; isNew?: boolean }) => void;
  setDeleteModal: (v: { open: boolean; product: any }) => void;
}): ColumnDef<any, any>[] {
  return [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => {
        const product = row.original;
        return (
          <div className="flex items-center gap-2">
            {product.image_url || product.image_path ? (
              <img
                src={product.image_url || product.image_path}
                alt=""
                className="w-8 h-8 rounded object-contain flex-shrink-0"
                onError={(e: any) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div
              className={`w-8 h-8 bg-gray-100 rounded flex items-center justify-center flex-shrink-0 ${product.image_url || product.image_path ? 'hidden' : ''}`}
              style={{ display: product.image_url || product.image_path ? 'none' : 'flex' }}
            >
              <Package className="w-4 h-4 text-gray-400" />
            </div>
            <span className="font-medium text-sm truncate" title={product.name}>
              {product.name}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: 'article_number',
      header: 'Art.Nr.',
      cell: ({ getValue }) => (
        <span className="font-mono text-xs text-gray-500">{getValue() || '-'}</span>
      ),
    },
    {
      accessorKey: 'category',
      header: 'Kat.',
      cell: ({ getValue }) => {
        const cat = getValue() as string;
        return (
          <span className="text-xs text-gray-600" title={cat}>
            {CATEGORY_SHORT[cat] || cat || '-'}
          </span>
        );
      },
    },
    {
      accessorKey: 'unit',
      header: 'Einh.',
      cell: ({ getValue }) => {
        const unit = getValue() as string;
        return (
          <span className="text-xs text-gray-600" title={unit}>
            {UNIT_SHORT[unit] || unit || '-'}
          </span>
        );
      },
    },
    // Supplier columns
    ...SUPPLIERS.map((supplier) => ({
      id: supplier.code,
      header: () => <span title={supplier.name}>{supplier.short}</span>,
      cell: ({ row }: { row: any }) => {
        const product = row.original;
        const info = getSupplierInfo(product.sources, supplier.name);
        const hasLink = info?.url || product.supplier_links?.some((l: any) =>
          l.name?.toLowerCase().includes(supplier.name.toLowerCase()) ||
          l.url?.includes(supplier.code)
        );
        const link = info?.url || product.supplier_links?.find((l: any) =>
          l.name?.toLowerCase().includes(supplier.name.toLowerCase()) ||
          l.url?.includes(supplier.code)
        )?.url;

        if (info?.price) {
          return (
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-0.5 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 px-1.5 py-0.5 rounded transition-colors"
              title={`${supplier.name}: ${Number(info.price_per_unit || info.price).toLocaleString('de-DE', { minimumFractionDigits: 2 })} EUR${info.package_size ? ` pro ${product.unit || 'Einheit'} (Gebinde: ${info.package_size})` : ''}`}
              onClick={(e) => e.stopPropagation()}
            >
              {Number(info.price_per_unit || (info.package_size ? info.price / info.package_size : info.price)).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </a>
          );
        }
        if (hasLink) {
          return (
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-xs text-amber-600 bg-amber-50 hover:bg-amber-100 px-1.5 py-0.5 rounded transition-colors"
              title={`${supplier.name} (kein Preis)`}
              onClick={(e) => e.stopPropagation()}
            >
              <Check className="w-3 h-3" />
            </a>
          );
        }
        return <span className="text-gray-300 text-xs">—</span>;
      },
    })),
    {
      id: 'actions',
      header: 'Aktionen',
      cell: ({ row }) => {
        const product = row.original;
        return (
          <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
            <Button
              variant="ghost"
              size="sm"
              icon={TrendingUp}
              onClick={() => setPriceModal({ open: true, product })}
              title="Preisvergleich"
              className="text-green-600 hover:text-green-700 hover:bg-green-50 p-1"
            />
            <Button
              variant="ghost"
              size="sm"
              icon={Edit}
              onClick={() => setEditModal({ open: true, product })}
              className="p-1"
            />
            <Button
              variant="ghost"
              size="sm"
              icon={Trash2}
              onClick={() => setDeleteModal({ open: true, product })}
              className="text-red-600 hover:text-red-700 hover:bg-red-50 p-1"
            />
          </div>
        );
      },
    },
  ];
}

// K.I.-Produktfoto-Analyse seit 21.10.2026 auf Eis (keine Claude-API, noch kein
// lokales Bildmodell) — Backend antwortet mit 503. Einfuegen/Ablegen von Bildern
// startet deshalb keine Analyse mehr; zum Reaktivieren hier auf true setzen.
const PRODUCT_PHOTO_AI_ENABLED = false;

const Produkte = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  const { data: products = [], isLoading: loading } = useProducts({ category: categoryFilter || undefined });
  const { data: categories = [] } = useProductCategories();
  const [editModal, setEditModal] = useState({ open: false, product: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, product: null });
  const [priceModal, setPriceModal] = useState({ open: false, product: null });
  const [aiAssistantOpen, setAiAssistantOpen] = useState(false);
  const [batchModalOpen, setBatchModalOpen] = useState(false);
  const [urlModalOpen, setUrlModalOpen] = useState(false);
  // AI Keyword Generation
  const generateKeywordsMutation = useMutation({
    mutationFn: () => api.post('/products/generate-keywords').then((r) => r.data),
    onSuccess: (data) => {
      if (data.updated > 0) {
        // message nennt bei mehr als 40 Produkten den Rest (Paket pro Klick, lokales LLM)
        toast.success(data.message || `${data.updated} Suchbegriffe generiert`);
      } else {
        toast.success('Alle Produkte haben bereits Suchbegriffe');
      }
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Fehler beim Generieren');
    },
  });
  const keywordsLoading = generateKeywordsMutation.isPending;
  const generateKeywords = () => generateKeywordsMutation.mutate();

  // OCI State
  const [ociPanelOpen, setOciPanelOpen] = useState(false);
  const [ociLoading, setOciLoading] = useState({});

  const { data: ociShops = [] } = useQuery({
    queryKey: ['oci', 'shops'],
    queryFn: () => api.get('/oci/shops').then((r) => r.data),
  });

  // Obeta Bulk-Update State
  const [obetaBulk, setObetaBulk] = useState({ active: false, total: 0, done: 0, updated: 0, errors: 0, log: [] });

  // OCI Punchout starten (generisch für jeden Shop)
  const handleOciLaunch = (supplierCode = 'zajadacz', supplierName = 'Zajadacz') => {
    const w = window.open(
      `/api/oci/launch/${supplierCode}`,
      'oci_punchout',
      'width=1200,height=800,scrollbars=yes,resizable=yes'
    );
    if (!w) {
      toast.error('Popup wurde blockiert. Bitte Popup-Blocker deaktivieren.');
      return;
    }
    toast.success(`${supplierName} Shop wird geöffnet...`);
  };

  // OCI Bulk-Update starten
  const handleOciBulk = (supplierCode, supplierName) => {
    // Obeta uses server-side SSE bulk-update
    if (supplierCode === 'obeta') {
      handleObetaBulkUpdate();
      return;
    }
    setOciLoading(prev => ({ ...prev, [supplierCode]: true }));
    const w = window.open(
      `/api/oci/bulk/${supplierCode}`,
      'oci_bulk',
      'width=600,height=500,scrollbars=yes,resizable=yes'
    );
    if (!w) {
      toast.error('Popup wurde blockiert.');
      setOciLoading(prev => ({ ...prev, [supplierCode]: false }));
      return;
    }
    toast.success(`${supplierName} Bulk-Update gestartet...`);
    // Reset loading after 5s (popup handles the rest)
    setTimeout(() => {
      setOciLoading(prev => ({ ...prev, [supplierCode]: false }));
    }, 5000);
  };

  // Obeta Bulk-Update via SSE
  const handleObetaBulkUpdate = async () => {
    setObetaBulk({ active: true, total: 0, done: 0, updated: 0, errors: 0, log: [], finished: false });
    try {
      const res = await fetch('/api/omd/bulk-update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${res.status}`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';
        for (const part of parts) {
          // Parse SSE: "event: xxx\ndata: {...}"
          let eventName = 'message';
          let dataStr = '';
          for (const line of part.split('\n')) {
            if (line.startsWith('event: ')) eventName = line.slice(7).trim();
            if (line.startsWith('data: ')) dataStr = line.slice(6);
          }
          if (!dataStr) continue;
          try {
            const data = JSON.parse(dataStr);
            setObetaBulk(prev => {
              const next = { ...prev };
              if (data.total && !prev.finished) next.total = data.total;

              if (eventName === 'status') {
                next.log = [...prev.log, data.message].slice(-30);
              }
              if (eventName === 'progress') {
                if (data.progress != null) next.done = data.progress;
                next.log = [...prev.log, `${data.step}: ${data.message}`].slice(-30);
              }
              if (eventName === 'error') {
                next.log = [...prev.log, `Fehler: ${data.message}`].slice(-30);
                next.errors = (prev.errors || 0) + 1;
              }
              if (eventName === 'done') {
                next.finished = true;
                next.updated = data.updated || 0;
                next.transferred = data.transferred || 0;
                next.callbackReceived = data.callbackReceived || false;
                next.errors = data.failed || 0;
                next.log = [...prev.log, data.message || 'Fertig'].slice(-30);
              }
              return next;
            });
          } catch (e) { /* skip invalid JSON */ }
        }
      }
      toast.success('Obeta Bulk-Update abgeschlossen');
      queryClient.invalidateQueries({ queryKey: ['products'] });
      // Auto-hide after 8s
      setTimeout(() => {
        setObetaBulk(prev => prev.finished ? { ...prev, active: false } : prev);
      }, 8000);
    } catch (err) {
      toast.error(`Obeta Bulk-Update Fehler: ${err.message}`);
      setObetaBulk(prev => ({ ...prev, log: [...prev.log, `FEHLER: ${err.message}`], finished: true }));
    }
  };

  // Global paste/drop state
  const [pastedImage, setPastedImage] = useState(null);
  const [pastedImagePreview, setPastedImagePreview] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const dragCounter = useRef(0);

  // Duplicate detection state
  const [duplicateMatches, setDuplicateMatches] = useState([]);
  const [isCheckingDuplicates, setIsCheckingDuplicates] = useState(false);
  const [selectedDuplicate, setSelectedDuplicate] = useState(null);
  const [isAddingPrice, setIsAddingPrice] = useState(false);

  // Handle file selection from paste or drop
  const handleFileSelect = useCallback((file) => {
    if (!file) return;
    if (!PRODUCT_PHOTO_AI_ENABLED) {
      toast.info('Die K.I.-Bildanalyse ist vorübergehend deaktiviert');
      return;
    }

    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast.error('Nur Bildformate (PNG, JPG, WEBP, GIF) erlaubt');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Bild zu groß (max. 10MB)');
      return;
    }

    setPastedImage(file);
    setAnalysisResult(null);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPastedImagePreview(e.target.result);
    };
    reader.readAsDataURL(file);

    // Auto-start analysis
    analyzeImage(file);
  }, []);

  // Analyze image with AI
  const analyzeImage = async (file) => {
    setIsAnalyzing(true);
    setDuplicateMatches([]);
    setSelectedDuplicate(null);

    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await api.post('/products/ai-analyze', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data.success && response.data.result) {
        const result = response.data.result;
        setAnalysisResult(result);

        // Check for duplicates after successful analysis
        if (result.productName || result.articleNumber) {
          setIsCheckingDuplicates(true);
          try {
            const dupResponse = await api.post('/products/find-similar', {
              productName: result.productName,
              articleNumber: result.articleNumber,
              category: result.category,
            });
            if (dupResponse.data.matches?.length > 0) {
              setDuplicateMatches(dupResponse.data.matches);
              console.log('Found duplicate matches:', dupResponse.data.matches);
            }
          } catch (dupErr) {
            console.error('Duplicate check error:', dupErr);
          } finally {
            setIsCheckingDuplicates(false);
          }
        }
      } else {
        toast.error(response.data.error || 'K.I. konnte keine Daten extrahieren');
        setAnalysisResult({});
      }
    } catch (err) {
      console.error('AI analysis error:', err);
      toast.error(err.response?.data?.error || 'Fehler bei der K.I. Analyse');
      setAnalysisResult({});
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Add price to existing product (duplicate handling)
  const addPriceToExistingProduct = async (existingProduct) => {
    if (!analysisResult) return;

    setIsAddingPrice(true);
    try {
      await api.post(`/products/${existingProduct.id}/sources`, {
        supplier: analysisResult.supplier || 'Unbekannt',
        price: analysisResult.price,
        unit: analysisResult.unit,
        article_number: analysisResult.articleNumber,
        url: analysisResult.url,
      });

      toast.success(`Preis von ${analysisResult.supplier || 'neuem Lieferanten'} zu "${existingProduct.name}" hinzugefügt`);
      closePasteCard();
      queryClient.invalidateQueries({ queryKey: ['products'] });
    } catch (err) {
      console.error('Add price error:', err);
      toast.error(err.response?.data?.error || 'Fehler beim Hinzufügen des Preises');
    } finally {
      setIsAddingPrice(false);
    }
  };

  // Save analyzed product
  const saveAnalyzedProduct = async () => {
    if (!analysisResult?.productName) {
      toast.error('Produktname erforderlich');
      return;
    }

    setIsSaving(true);

    try {
      const formData = new FormData();
      formData.append('name', analysisResult.productName || '');
      formData.append('article_number', analysisResult.articleNumber || '');
      formData.append('price', analysisResult.price || '');
      formData.append('unit', analysisResult.unit || '');
      formData.append('quantity', analysisResult.quantity || '');
      formData.append('category', analysisResult.category || '');
      formData.append('search_keywords', analysisResult.searchTerms || '');

      if (analysisResult.supplier && analysisResult.url) {
        formData.append('supplier_links', JSON.stringify([{
          name: analysisResult.supplier,
          url: analysisResult.url,
        }]));
      }
      formData.append('supplier', analysisResult.supplier || '');
      formData.append('supplier_link', analysisResult.url || '');

      if (pastedImage) {
        formData.append('image', pastedImage);
      }

      const response = await api.post('/products/save-with-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data) {
        toast.success('Produkt erstellt: ' + response.data.name);
        closePasteCard();
        queryClient.invalidateQueries({ queryKey: ['products'] });
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Fehler beim Speichern');
    } finally {
      setIsSaving(false);
    }
  };

  // Close paste card
  const closePasteCard = () => {
    setPastedImage(null);
    setPastedImagePreview(null);
    setAnalysisResult(null);
    setIsAnalyzing(false);
    setDuplicateMatches([]);
    setSelectedDuplicate(null);
    setIsCheckingDuplicates(false);
  };

  // Open product form with pre-filled AI data for editing
  const openProductFormWithAIData = () => {
    if (!analysisResult) return;

    // Map AI result to product format that ProductForm expects
    const prefilledProduct = {
      name: analysisResult.productName || '',
      article_number: analysisResult.articleNumber || '',
      price: analysisResult.price || '',
      unit: analysisResult.unit || '',
      quantity: analysisResult.quantity || '',
      category: analysisResult.category || '',
      supplier: analysisResult.supplier || '',
      supplier_link: analysisResult.url || '',
      supplier_links: analysisResult.supplier && analysisResult.url
        ? [{ name: analysisResult.supplier, url: analysisResult.url }]
        : [],
      search_keywords: analysisResult.searchTerms || '',
    };

    // Open the edit modal with pre-filled data
    setEditModal({ open: true, product: prefilledProduct, isNew: true });
    closePasteCard();
  };

  // Global paste handler - nur aktiv wenn KEIN Modal offen ist
  useEffect(() => {
    const handlePaste = (e) => {
      // WICHTIG: Nicht reagieren wenn ein Modal offen ist (BatchModal, AI-Assistent oder URL-Modal)
      // Diese Modals haben ihre eigenen paste-Handler bzw. brauchen keinen
      if (batchModalOpen || aiAssistantOpen || urlModalOpen) {
        return;
      }

      // Don't capture if user is typing in an input field
      const activeElement = document.activeElement;
      const isInputField = activeElement?.tagName === 'INPUT' ||
                          activeElement?.tagName === 'TEXTAREA' ||
                          activeElement?.isContentEditable;

      // Nicht reagieren wenn User in einem Eingabefeld tippt
      if (isInputField) {
        return;
      }

      // Only handle paste if it contains an image
      const items = e.clipboardData?.items;
      if (!items) return;

      for (const item of items) {
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          const file = item.getAsFile();
          handleFileSelect(file);
          return;
        }
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [handleFileSelect, batchModalOpen, aiAssistantOpen, urlModalOpen]);

  // Global drag & drop handlers
  useEffect(() => {
    const handleDragEnter = (e) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current++;
      if (e.dataTransfer?.types?.includes('Files')) {
        setIsDragging(true);
      }
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current--;
      if (dragCounter.current === 0) {
        setIsDragging(false);
      }
    };

    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      dragCounter.current = 0;

      const files = e.dataTransfer?.files;
      if (files && files.length > 0) {
        const file = files[0];
        if (file.type.startsWith('image/')) {
          handleFileSelect(file);
        }
      }
    };

    document.addEventListener('dragenter', handleDragEnter);
    document.addEventListener('dragleave', handleDragLeave);
    document.addEventListener('dragover', handleDragOver);
    document.addEventListener('drop', handleDrop);

    return () => {
      document.removeEventListener('dragenter', handleDragEnter);
      document.removeEventListener('dragleave', handleDragLeave);
      document.removeEventListener('dragover', handleDragOver);
      document.removeEventListener('drop', handleDrop);
    };
  }, [handleFileSelect]);

  const filteredProducts = products.filter((p) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      p.name?.toLowerCase().includes(searchLower) ||
      p.article_number?.toLowerCase().includes(searchLower)
    );
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => productsApi.delete(id),
    onSuccess: () => {
      toast.success('Produkt gelöscht');
      setDeleteModal({ open: false, product: null });
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
    onError: (err: any) => {
      toast.error(err.message || 'Fehler beim Löschen');
    },
  });

  const handleDelete = () => {
    if (!deleteModal.product) return;
    deleteMutation.mutate(deleteModal.product.id);
  };

  const categoryOptions = [
    { value: '', label: 'Alle Kategorien' },
    ...categories.map((c) => ({ value: c, label: c })),
  ];

  return (
    <div>
      <PageHeader
        title="Produktdatenbank"
        description={`${products.length} Produkte`}
        actions={
          <div className="flex gap-2 flex-wrap">
            {/* OCI Import Panel */}
            <Button
              variant="outline"
              onClick={() => setOciPanelOpen(true)}
              className="relative"
            >
              <ShoppingCart className="w-4 h-4 mr-2" />
              OCI Import
            </Button>
            {/* Shop-Buttons: Punchout für jeden aktiven Shop */}
            {ociShops.filter(s => s.ociReady).map(shop => (
              <Button
                key={shop.code}
                variant="outline"
                onClick={() => handleOciLaunch(shop.code, shop.name)}
                title={`${shop.name} Shop öffnen (OCI Punchout)`}
              >
                <Store className="w-4 h-4 mr-1" />
                {shop.name}
              </Button>
            ))}
            {/* Bulk-Update Dropdown */}
            <div className="relative group">
              <Button variant="outline" title="Alle Preise aktualisieren">
                <RefreshCw className="w-4 h-4 mr-2" />
                Bulk-Update
              </Button>
              <div className="absolute right-0 top-full w-48 pt-2 hidden group-hover:block z-30">
              <div className="bg-white rounded-lg shadow-xl border py-1">
                {SUPPLIERS.map(s => {
                  const shop = ociShops.find(os => os.code === s.code);
                  const isReady = shop?.ociReady;
                  return (
                    <button
                      key={s.code}
                      className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 ${
                        isReady
                          ? 'hover:bg-gray-50 text-gray-800'
                          : 'text-gray-400 cursor-not-allowed'
                      }`}
                      onClick={() => isReady && handleOciBulk(s.code, s.name)}
                      disabled={!isReady || ociLoading[s.code]}
                    >
                      {ociLoading[s.code] ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4" />
                      )}
                      <span>{s.name}</span>
                      {!isReady && (
                        <span className="ml-auto text-xs text-gray-400">inaktiv</span>
                      )}
                    </button>
                  );
                })}
              </div>
              </div>
            </div>
            {/* AI Suchbegriffe */}
            <Button
              variant="outline"
              onClick={generateKeywords}
              disabled={keywordsLoading}
              title="Suchbegriffe per AI für alle Produkte ohne Keywords generieren"
            >
              {keywordsLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4 mr-2" />
              )}
              Keywords
            </Button>
            {/* Neues Produkt */}
            <Button
              variant="primary"
              icon={Plus}
              onClick={() => setEditModal({ open: true, product: null })}
            >
              Neues Produkt
            </Button>
          </div>
        }
      />

      {/* Filters */}
      <Card className="mb-6" padding={false}><Card.Content className="flex flex-wrap gap-4 px-4 py-3">
        <div className="relative min-w-[200px] max-w-sm flex-1"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" /><Input placeholder="Suche..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" /></div>
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="flex h-9 w-48 rounded-md border border-input bg-white px-3 py-1 text-sm">
          <option value="">Alle Kategorien</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </Card.Content></Card>

      {/* Table */}
      {loading ? (
        <Spinner />
      ) : (
        <DataTable
          columns={produkteColumns({
            setPriceModal,
            setEditModal,
            setDeleteModal,
          })}
          data={filteredProducts}
          pageSize={50}
          emptyText="Keine Produkte gefunden"
        />
      )}

      {/* Edit Modal */}
      <Modal
        isOpen={editModal.open}
        onClose={() => setEditModal({ open: false, product: null })}
        title={editModal.product?.id ? 'Produkt bearbeiten' : 'Neues Produkt'}
        size="lg"
      >
        <ProductForm
          product={editModal.product}
          categories={categories}
          onClose={() => setEditModal({ open: false, product: null })}
          onSuccess={() => {
            setEditModal({ open: false, product: null });
            queryClient.invalidateQueries({ queryKey: ['products'] });
            toast.success(editModal.product?.id ? 'Produkt aktualisiert' : 'Produkt erstellt');
          }}
        />
      </Modal>

      {/* Delete Modal */}
      <Modal.Confirm
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, product: null })}
        onConfirm={handleDelete}
        title="Produkt löschen"
        message={`Möchten Sie das Produkt "${deleteModal.product?.name}" wirklich löschen?`}
      />

      {/* AI Assistant Modal */}
      <ProductAIAssistant
        isOpen={aiAssistantOpen}
        onClose={() => setAiAssistantOpen(false)}
        onProductSaved={(newProduct) => {
          queryClient.invalidateQueries({ queryKey: ['products'] });
          toast.success('Produkt via K.I. erstellt: ' + newProduct.name);
        }}
      />

      {/* Batch K.I. Modal */}
      <ProductBatchModal
        isOpen={batchModalOpen}
        onClose={() => setBatchModalOpen(false)}
        onProductSaved={(newProduct) => {
          queryClient.invalidateQueries({ queryKey: ['products'] });
          toast.success('Produkt mit K.I. erstellt: ' + newProduct.name);
        }}
      />

      {/* URL Modal */}
      <ProductUrlModal
        isOpen={urlModalOpen}
        onClose={() => setUrlModalOpen(false)}
        onProductSaved={(newProduct) => {
          queryClient.invalidateQueries({ queryKey: ['products'] });
          toast.success('Produkt per URL erstellt: ' + newProduct.name);
        }}
      />

      {/* Preisvergleich Modal */}
      <Modal
        isOpen={priceModal.open}
        onClose={() => setPriceModal({ open: false, product: null })}
        title="Preisvergleich"
        size="xl"
      >
        {priceModal.product && (
          <PriceComparison
            productId={priceModal.product.id}
            productName={priceModal.product.name}
            articleNumber={priceModal.product.article_number}
            onClose={() => setPriceModal({ open: false, product: null })}
          />
        )}
      </Modal>

      {/* Global Drag & Drop Overlay */}
      {isDragging && (
        <div className="fixed inset-0 bg-primary-500/20 backdrop-blur-sm z-50 flex items-center justify-center pointer-events-none">
          <div className="bg-white rounded-2xl shadow-2xl p-12 text-center border-4 border-dashed border-primary-500">
            <Upload className="w-16 h-16 text-primary-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Bild hier ablegen</h2>
            <p className="text-gray-500">Das Bild wird automatisch analysiert</p>
          </div>
        </div>
      )}

      {/* Floating Analysis Card */}
      {pastedImagePreview && (
        <div className="fixed bottom-6 right-6 z-40 w-96 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-primary-500 to-primary-600 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-white">
              <Bot className="w-5 h-5" />
              <span className="font-medium">K.I. Bildanalyse</span>
            </div>
            <button
              onClick={closePasteCard}
              className="text-white/80 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Image Preview */}
          <div className="p-4 bg-gray-50 border-b">
            <img
              src={pastedImagePreview}
              alt="Screenshot"
              className="max-h-32 mx-auto rounded shadow"
            />
          </div>

          {/* Content */}
          <div className="p-4">
            {isAnalyzing ? (
              <div className="text-center py-4">
                <Loader2 className="w-8 h-8 text-primary-500 animate-spin mx-auto mb-3" />
                <p className="text-gray-600 font-medium">K.I. analysiert Bild...</p>
                <p className="text-gray-400 text-sm mt-1">Produktdaten werden extrahiert</p>
              </div>
            ) : analysisResult ? (
              <div className="space-y-3">
                {analysisResult.productName ? (
                  <>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Produkt</p>
                      <p className="font-medium text-gray-800 truncate">{analysisResult.productName}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      {analysisResult.articleNumber && (
                        <div>
                          <p className="text-xs text-gray-500">Art.Nr.</p>
                          <p className="text-gray-700 font-mono">{analysisResult.articleNumber}</p>
                        </div>
                      )}
                      {analysisResult.price && (
                        <div>
                          <p className="text-xs text-gray-500">Preis</p>
                          <p className="text-gray-700 font-medium">{analysisResult.price} €</p>
                        </div>
                      )}
                      {analysisResult.supplier && (
                        <div>
                          <p className="text-xs text-gray-500">Lieferant</p>
                          <p className="text-gray-700">{analysisResult.supplier}</p>
                        </div>
                      )}
                      {analysisResult.category && (
                        <div>
                          <p className="text-xs text-gray-500">Kategorie</p>
                          <p className="text-gray-700">{analysisResult.category}</p>
                        </div>
                      )}
                    </div>
                    {/* Duplicate Warning */}
                    {isCheckingDuplicates && (
                      <div className="bg-gray-50 rounded-lg p-3 text-center">
                        <Loader2 className="w-4 h-4 text-gray-400 animate-spin mx-auto mb-1" />
                        <p className="text-xs text-gray-500">Prüfe auf Duplikate...</p>
                      </div>
                    )}

                    {duplicateMatches.length > 0 && !isCheckingDuplicates && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                        <div className="flex items-start gap-2 mb-2">
                          <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-amber-800">
                              Ähnliches Produkt gefunden!
                            </p>
                            <p className="text-xs text-amber-600 mt-0.5">
                              Möchten Sie den Preis zum bestehenden Produkt hinzufügen?
                            </p>
                          </div>
                        </div>
                        <div className="space-y-2 mt-3">
                          {duplicateMatches.slice(0, 2).map((match) => (
                            <div
                              key={match.id}
                              className={`p-2 rounded border cursor-pointer transition-colors ${
                                selectedDuplicate?.id === match.id
                                  ? 'bg-amber-100 border-amber-400'
                                  : 'bg-white border-gray-200 hover:border-amber-300'
                              }`}
                              onClick={() => setSelectedDuplicate(match)}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-800 truncate">
                                    {match.name}
                                  </p>
                                  <div className="flex items-center gap-2 text-xs text-gray-500">
                                    {match.similarity && (
                                      <span className="bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                                        {Math.round(match.similarity * 100)}% Match
                                      </span>
                                    )}
                                    {match.matchType === 'exact_article' && (
                                      <span className="truncate">Exakte Artikelnummer</span>
                                    )}
                                    {match.matchType === 'fuzzy_technical' && (
                                      <span className="truncate">Technische Daten</span>
                                    )}
                                  </div>
                                  {match.existingSources?.length > 0 && (
                                    <p className="text-xs text-gray-400 mt-1">
                                      Bereits {match.existingSources.length} Lieferant{match.existingSources.length > 1 ? 'en' : ''}
                                    </p>
                                  )}
                                </div>
                                {selectedDuplicate?.id === match.id && (
                                  <Check className="w-4 h-4 text-amber-600 flex-shrink-0" />
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                        {selectedDuplicate && (
                          <Button
                            variant="primary"
                            size="sm"
                            className="w-full mt-3 bg-amber-500 hover:bg-amber-600"
                            onClick={() => addPriceToExistingProduct(selectedDuplicate)}
                            loading={isAddingPrice}
                          >
                            <PlusCircle className="w-4 h-4 mr-1" />
                            Preis zu "{selectedDuplicate.name.substring(0, 20)}..." hinzufügen
                          </Button>
                        )}
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex gap-2 pt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={openProductFormWithAIData}
                        className="flex-1"
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Bearbeiten
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={saveAnalyzedProduct}
                        loading={isSaving}
                        className="flex-1"
                        title={duplicateMatches.length > 0 ? 'Trotzdem als neues Produkt speichern' : 'Produkt speichern'}
                      >
                        <Save className="w-4 h-4 mr-1" />
                        {duplicateMatches.length > 0 ? 'Neu anlegen' : 'Speichern'}
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-2">
                    <p className="text-gray-500 text-sm mb-3">Keine Produktdaten erkannt</p>
                    <Button variant="outline" size="sm" onClick={() => {
                      setEditModal({ open: true, product: null });
                      closePasteCard();
                    }}>
                      <Edit className="w-4 h-4 mr-1" />
                      Manuell eingeben
                    </Button>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* Paste Hint (subtle) */}
      {PRODUCT_PHOTO_AI_ENABLED && !pastedImagePreview && !aiAssistantOpen && (
        <div className="fixed bottom-6 right-6 z-30">
          <div className="bg-gray-800/80 backdrop-blur text-white text-sm px-4 py-2 rounded-lg flex items-center gap-2 shadow-lg">
            <Clipboard className="w-4 h-4 text-gray-300" />
            <span className="text-gray-200">Strg+V zum Einfügen</span>
          </div>
        </div>
      )}

      {/* Obeta Bulk-Update Progress */}
      {obetaBulk.active && (
        <div className="fixed bottom-4 left-4 z-50 w-96 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
          <div className={`${obetaBulk.finished ? 'bg-gradient-to-r from-green-600 to-green-500' : 'bg-blue-600'} text-white px-4 py-2 flex items-center justify-between`}>
            <span className="font-medium text-sm flex items-center gap-2">
              {obetaBulk.finished ? (
                <Check className="w-4 h-4" />
              ) : (
                <RefreshCw className="w-4 h-4 animate-spin" />
              )}
              Obeta OMD-Update
            </span>
            <button onClick={() => setObetaBulk(prev => ({ ...prev, active: false }))} className="text-white/80 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="p-3">
            <div className="flex justify-between text-xs text-gray-600 mb-1">
              <span>{obetaBulk.done} / {obetaBulk.total || '...'}</span>
              <span className="flex gap-2">
                {obetaBulk.finished && obetaBulk.updated > 0 && (
                  <span className="text-green-600">{obetaBulk.updated} aktualisiert</span>
                )}
                {obetaBulk.finished && obetaBulk.transferred > 0 && !obetaBulk.updated && (
                  <span className="text-blue-600">{obetaBulk.transferred} gesendet</span>
                )}
                {obetaBulk.errors > 0 && <span className="text-red-600">{obetaBulk.errors} Fehler</span>}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`${obetaBulk.finished ? 'bg-green-500' : 'bg-blue-600'} h-2 rounded-full transition-all duration-300`}
                style={{ width: obetaBulk.finished ? '100%' : (obetaBulk.total ? `${(obetaBulk.done / obetaBulk.total) * 100}%` : '0%') }}
              />
            </div>
            <div className="mt-2 max-h-32 overflow-y-auto text-xs font-mono text-gray-500 space-y-0.5">
              {obetaBulk.log.map((line, i) => (
                <div key={i} className={line.includes('Fehler') || line.includes('error') || line.includes('FEHLER') ? 'text-red-500' : ''}>{line}</div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* OCI Import Panel */}
      <OciImportPanel
        isOpen={ociPanelOpen}
        onClose={() => setOciPanelOpen(false)}
        onProductsChanged={() => queryClient.invalidateQueries({ queryKey: ['products'] })}
      />
    </div>
  );
};

// Verfügbarkeits-Optionen für Lieferanten
const AVAILABILITY_OPTIONS = [
  { value: 'unknown', label: '— Unbekannt —' },
  { value: 'in_stock', label: 'Auf Lager' },
  { value: 'limited', label: 'Wenig verfügbar' },
  { value: 'out_of_stock', label: 'Nicht verfügbar' },
  { value: 'on_request', label: 'Auf Anfrage' },
];

// Helper: Initialisiere Lieferanten-Daten aus sources
const initSupplierData = (product) => {
  const data = {};
  SUPPLIERS.forEach(s => {
    // Suche in sources
    const source = product?.sources?.find(src =>
      src.supplier?.toLowerCase().includes(s.name.toLowerCase())
    );
    // Suche in supplier_links
    const link = product?.supplier_links?.find(l =>
      l.name?.toLowerCase().includes(s.name.toLowerCase()) ||
      l.url?.includes(s.code)
    );

    data[s.code] = {
      url: source?.url || link?.url || '',
      price: source?.price || '',
      availability: source?.availability || 'unknown',
      sku: source?.article_number || '',
    };
  });
  return data;
};

// Product Form Component
const ProductForm = ({ product, categories, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: product?.name || '',
    article_number: product?.article_number || '',
    unit: product?.unit || '',
    quantity: product?.quantity || '',
    category: product?.category || '',
    search_keywords: Array.isArray(product?.search_keywords) ? product.search_keywords.join('; ') : (product?.search_keywords || ''),
    image_url: product?.image_url || '',
  });
  const [supplierData, setSupplierData] = useState(() => initSupplierData(product));
  const [saving, setSaving] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(product?.image_url || product?.image_path || null);
  const imageInputRef = useRef(null);
  const formRef = useRef(null);
  // Handle image from file (either from input or paste)
  const handleImageFile = useCallback((file) => {
    if (!file) return;

    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast.error('Nur Bildformate (PNG, JPG, WEBP, GIF) erlaubt');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Bild zu groß (max. 5MB)');
      return;
    }

    setImageFile(file);
    setFormData(prev => ({ ...prev, image_url: '' }));

    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
    };
    reader.readAsDataURL(file);

    toast.success('Bild aus Zwischenablage eingefügt');
  }, []);

  // Paste handler for clipboard images (Ctrl+V)
  useEffect(() => {
    const handlePaste = (e) => {
      // Check if paste event is within the form
      if (!formRef.current?.contains(document.activeElement) &&
          !formRef.current?.contains(e.target)) {
        return;
      }

      const items = e.clipboardData?.items;
      if (!items) return;

      for (const item of items) {
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          e.stopPropagation();
          const file = item.getAsFile();
          handleImageFile(file);
          return;
        }
      }
    };

    document.addEventListener('paste', handlePaste, true);
    return () => document.removeEventListener('paste', handlePaste, true);
  }, [handleImageFile]);

  // Handle image file selection from input
  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    handleImageFile(file);
  };

  // Remove image
  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setFormData({ ...formData, image_url: '' });
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  const unitOptions = [
    { value: '', label: 'Einheit wählen...' },
    ...UNITS.map((u) => ({ value: u, label: u })),
  ];

  const categoryOptions = [
    { value: '', label: 'Kategorie wählen...' },
    ...CATEGORIES.map((c) => ({ value: c, label: c })),
    { value: '__new__', label: '+ Neue Kategorie' },
  ];

  // Helper: Preis parsen und formatieren
  const parsePrice = (val) => {
    if (!val) return null;
    return parseFloat(String(val).replace(',', '.'));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Name ist erforderlich');
      return;
    }

    setSaving(true);
    try {
      // Lieferanten-Links aus supplierData erstellen
      const supplierLinks = SUPPLIERS
        .filter(s => supplierData[s.code]?.url)
        .map(s => ({
          name: s.name,
          url: supplierData[s.code].url,
        }));

      // Bester Preis ermitteln (für products.price)
      const prices = SUPPLIERS
        .map(s => parsePrice(supplierData[s.code]?.price))
        .filter(p => p !== null && !isNaN(p));
      const bestPrice = prices.length > 0 ? Math.min(...prices) : null;

      // Haupt-Lieferant (mit günstigstem Preis)
      const bestSupplier = SUPPLIERS.find(s => {
        const p = parsePrice(supplierData[s.code]?.price);
        return p === bestPrice;
      });

      const data = {
        name: formData.name,
        article_number: formData.article_number,
        price: bestPrice,
        unit: formData.unit,
        quantity: formData.quantity ? parseInt(formData.quantity, 10) : null,
        category: formData.category === '__new__' ? newCategory : formData.category,
        supplier: bestSupplier?.name || null,
        supplier_link: bestSupplier ? supplierData[bestSupplier.code]?.url : null,
        supplier_links: supplierLinks,
        search_keywords: formData.search_keywords
          .split(';')
          .map((k) => k.trim())
          .filter(Boolean),
        image_url: formData.image_url || null,
      };

      // If image file is selected, upload it via multipart
      if (imageFile) {
        const imageFormData = new FormData();
        Object.entries(data).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            if (typeof value === 'object') {
              imageFormData.append(key, JSON.stringify(value));
            } else {
              imageFormData.append(key, value);
            }
          }
        });
        imageFormData.append('image', imageFile);

        if (product?.id) {
          await api.put(`/products/${product.id}`, imageFormData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } else {
          const newProduct = await api.post('/products/save-with-image', imageFormData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });

          // Lieferanten-Preise für neues Produkt
          if (newProduct?.data?.id) {
            for (const s of SUPPLIERS) {
              const sd = supplierData[s.code];
              if (sd?.url || sd?.price) {
                try {
                  await api.post(`/products/${newProduct.data.id}/sources`, {
                    supplier: s.name,
                    price: parsePrice(sd.price),
                    url: sd.url || null,
                    article_number: sd.sku || null,
                  });
                } catch (err) {
                  console.log('Source create info:', err.message);
                }
              }
            }
          }
          onSuccess();
          return;
        }
      } else if (product?.id) {
        await productsApi.update(product.id, data);

        // Lieferanten-Preise separat speichern
        for (const s of SUPPLIERS) {
          const sd = supplierData[s.code];
          if (sd?.url || sd?.price) {
            try {
              await api.post(`/products/${product.id}/sources`, {
                supplier: s.name,
                price: parsePrice(sd.price),
                url: sd.url || null,
                article_number: sd.sku || null,
              });
            } catch (err) {
              console.log('Source update info:', err.message);
            }
          }
        }
      } else {
        const newProduct = await productsApi.create(data);

        // Lieferanten-Preise für neues Produkt
        if (newProduct?.id) {
          for (const s of SUPPLIERS) {
            const sd = supplierData[s.code];
            if (sd?.url || sd?.price) {
              try {
                await api.post(`/products/${newProduct.id}/sources`, {
                  supplier: s.name,
                  price: parsePrice(sd.price),
                  url: sd.url || null,
                  article_number: sd.sku || null,
                });
              } catch (err) {
                console.log('Source create info:', err.message);
              }
            }
          }
        }
      }
      onSuccess();
    } catch (err) {
      toast.error(err.message || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const updateSupplierField = (code, field, value) => {
    setSupplierData(prev => ({
      ...prev,
      [code]: { ...prev[code], [field]: value },
    }));
  };

  return (
    <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
      {/* Zeile 1: Produktname */}
      <Input
        label="Produktname *"
        value={formData.name}
        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        required
      />

      {/* Zeile 2: Artikelnummer, Menge */}
      <div className="grid grid-cols-2 gap-4">
        <Input
          label="Artikelnummer (Hersteller)"
          value={formData.article_number}
          onChange={(e) => setFormData({ ...formData, article_number: e.target.value })}
          placeholder="z.B. 0106826"
        />
        <Input
          label="Menge pro VE"
          type="number"
          value={formData.quantity}
          onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
          placeholder="z.B. 500"
        />
      </div>

      {/* Zeile 3: Einheit, Kategorie */}
      <div className="grid grid-cols-2 gap-4">
        <Select
          label="Einheit"
          value={formData.unit}
          onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
          options={unitOptions}
        />
        <Select
          label="Kategorie"
          value={formData.category}
          onChange={(e) => setFormData({ ...formData, category: e.target.value })}
          options={categoryOptions}
        />
      </div>

      {formData.category === '__new__' && (
        <Input
          label="Neue Kategorie"
          value={newCategory}
          onChange={(e) => setNewCategory(e.target.value)}
          placeholder="Name der neuen Kategorie"
        />
      )}

      {/* Produktbild */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Produktbild</label>
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageSelect}
          className="hidden"
        />

        {imagePreview ? (
          <div className="relative inline-block">
            <img
              src={imagePreview}
              alt="Produktbild Vorschau"
              className="w-32 h-32 object-cover rounded-lg border border-gray-200"
            />
            <button
              type="button"
              onClick={removeImage}
              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={() => imageInputRef.current?.click()}
              className="flex items-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-400 hover:bg-gray-50 transition-colors"
            >
              <ImageIcon className="w-5 h-5 text-gray-400" />
              <div className="text-left">
                <span className="text-sm text-gray-600 block">Bild hochladen</span>
                <span className="text-xs text-gray-400">oder <kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-500 font-mono">Ctrl+V</kbd> für Screenshot</span>
              </div>
            </button>
          </div>
        )}

        {/* URL-Eingabe als Alternative */}
        <div className="mt-3">
          <Input
            label=""
            placeholder="Oder Bild-URL eingeben..."
            value={formData.image_url}
            onChange={(e) => {
              setFormData({ ...formData, image_url: e.target.value });
              if (e.target.value) {
                setImagePreview(e.target.value);
                setImageFile(null);
              }
            }}
            className="text-sm"
          />
        </div>
      </div>

      {/* Zeile 4: Großhändler-Preise */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">Großhändler-Preise</label>
        <div className="space-y-3 bg-gray-50 rounded-lg p-4">
          {SUPPLIERS.map(supplier => (
            <div key={supplier.code} className="bg-white rounded-lg border p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-medium text-gray-800">{supplier.name}</span>
                <span className="text-xs text-gray-400">({supplier.short})</span>
              </div>
              <div className="grid grid-cols-12 gap-2">
                <div className="col-span-6">
                  <Input
                    placeholder="URL zum Produkt"
                    value={supplierData[supplier.code]?.url || ''}
                    onChange={(e) => updateSupplierField(supplier.code, 'url', e.target.value)}
                    className="text-sm"
                  />
                </div>
                <div className="col-span-2">
                  <Input
                    placeholder="Preis"
                    type="text"
                    value={supplierData[supplier.code]?.price || ''}
                    onChange={(e) => updateSupplierField(supplier.code, 'price', e.target.value)}
                    className="text-sm"
                  />
                </div>
                <div className="col-span-4">
                  <Select
                    value={supplierData[supplier.code]?.availability || 'unknown'}
                    onChange={(e) => updateSupplierField(supplier.code, 'availability', e.target.value)}
                    options={AVAILABILITY_OPTIONS}
                    className="text-sm"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Der günstigste Preis wird automatisch als Hauptpreis verwendet.
        </p>
      </div>

      {/* Zeile 5: Suchbegriffe */}
      <Input
        label="Suchbegriffe (Semikolon-getrennt)"
        value={formData.search_keywords}
        onChange={(e) => setFormData({ ...formData, search_keywords: e.target.value })}
        placeholder="NYM; NYM-J; 3x2,5"
        helper="Diese Begriffe helfen beim Finden des Produkts"
      />

      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button variant="outline" type="button" onClick={onClose}>
          Abbrechen
        </Button>
        <Button variant="primary" type="submit" loading={saving}>
          Speichern
        </Button>
      </div>
    </form>
  );
};

export default Produkte;
